import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { IKizDTO } from "../../libs/coreapi-dto/dirs/kiz";
import { IEntitySimpleDTO } from "../../libs/coreapi-dto/@types/common";
import renderGlobalAlert from "./useGlobalAlert";
import { IDisposalReasonGismtDTO } from "../../libs/coreapi-dto/dirs/disposalReasonGismt";
import { DisposalReasonGismtDataProvider } from "../../Services/DataProviders/DisposalReasonGismtDataProvider";
import { useAppContext } from "../providers/appContextProvider";

interface IDocumentDataWithItems {
  items: any[];
}

interface IUseDisposalReasonGismtProps<T> {
    data: T;
    setData: React.Dispatch<React.SetStateAction<T>>;
    getKizs: (data: T) => IKizDTO[]; // функция извлечения списка КИЗ из data
    getIdDisposalReasonGismtGlobal: (data: T) => string | undefined; // извлекает ID причины выбытия ГИС МТ
    getIsWithdrawalGismtForUnscannedLots: (data: T) => boolean | undefined; // извлекат флаг режим выбытия ГИС МТ без подтверждения
}

export const useDisposalReasonGismt = <T extends IDocumentDataWithItems,>({
    data,
    setData,
    getKizs,
    getIdDisposalReasonGismtGlobal,
    getIsWithdrawalGismtForUnscannedLots
}: IUseDisposalReasonGismtProps<T>) => {

    const appContext = useAppContext();
    const disposalReasonGismtDP = new DisposalReasonGismtDataProvider(appContext.coreApiService);
    
    const { t } = useTranslation();

    const [disposalGismtForUnscannedLotsMode, setDisposalGismtForUnscannedLotsMode] = useState(false);
    const [disposalReasonGismt, setDisposalReasonGismt] = useState<IEntitySimpleDTO | null>(null);
    const [disposalReasonGismtEnabled, setDisposalReasonGismtEnabled] = useState(true);
    const [disposalGismtForUnscannedLotsModeEnabled, setDisposalGismtForUnscannedLotsModeEnabled] = useState(true);

    const isDisposalReasonGismtLoadingRef = useRef(false);

    // Определяем, включена ли ГИС МТ
    useEffect(() => {
        const isEmptyDocument: boolean = !data || !data.items || !Array.isArray(data.items) || data.items.length === 0;
        if (disposalGismtForUnscannedLotsMode) {
            setDisposalReasonGismtEnabled(true);
            setDisposalGismtForUnscannedLotsModeEnabled(isEmptyDocument); // Менять режим допустимо только если нет строк
        } else
        if (isEmptyDocument) {
            setDisposalReasonGismt(null);
            setDisposalReasonGismtEnabled(false);
            setDisposalGismtForUnscannedLotsModeEnabled(true);
        } else {
            const kizs = getKizs(data);
            const isEnabled = Array.isArray(kizs) && kizs.length > 0 && kizs.every(x => x && x.goodsGroup === "WTRU");
            setDisposalReasonGismtEnabled(isEnabled);
            setDisposalGismtForUnscannedLotsModeEnabled(false);
        }
    }, [data.items]);

    // Синхронизация состояния disposalGismtForUnscannedLotsMode с data.isWithdrawalGismtForUnscannedLots
    useEffect(() => {
        const isModeForUnscannedOn = getIsWithdrawalGismtForUnscannedLots(data);
        if (typeof isModeForUnscannedOn === 'boolean') {
            setDisposalGismtForUnscannedLotsMode(isModeForUnscannedOn);
        }
    }, [getIsWithdrawalGismtForUnscannedLots(data)]);

    // Загрузка причины списания при изменении id
    useEffect(() => {
        const id = getIdDisposalReasonGismtGlobal(data);
        if (id && !isDisposalReasonGismtLoadingRef.current) {
            disposalReasonGismtDP.getById(id, (reason: IDisposalReasonGismtDTO) => {
                setDisposalReasonGismt({
                    idGlobal: reason.idGlobal,
                    displayName: reason.name,
                });
            });
        }
    }, [getIdDisposalReasonGismtGlobal(data)]);

    const updateDisposalGismtForUnscannedLotsMode = (value: boolean) => {
        setDisposalGismtForUnscannedLotsMode(value);
        setData(prev => ({ ...prev, isWithdrawalGismtForUnscannedLots: value }));
    };

    const updateDisposalReasonGismt = (entity: IEntitySimpleDTO | null, setter?: (value: string | undefined) => void) => {
        if (entity != null) {
            const kizs = getKizs(data);

            if (kizs === undefined || kizs.length === 0 || kizs.some(x => x === undefined)) {
                renderGlobalAlert({
                    variant: "error",
                    statusCode: 500,
                    title: t("withdrawal.emptyKiz"),
                });
                return;
            }

            const wtruKizs = kizs.filter(x => x.goodsGroup === "WTRU");
            const uniqueGoodsGroupCodes = [
                ...new Set(wtruKizs.map(kiz => kiz.goodsGroupCode).filter((code): code is number => code != null && code > 0))
            ];

            if (wtruKizs.length === 0 && !disposalGismtForUnscannedLotsMode) {
                renderGlobalAlert({
                    variant: "error",
                    statusCode: 500,
                    title: t("withdrawal.invalidGoodsGroups"),
                });
                return;
            }

            disposalReasonGismtDP.getById(entity.idGlobal, (reason: IDisposalReasonGismtDTO) => {
                isDisposalReasonGismtLoadingRef.current = true;

                const validGoodsGroupsSet = new Set(reason.validGoodsGroups);

                const allValid = reason.validGoodsGroups.length === 0 || uniqueGoodsGroupCodes.length === 0 ||
                    (reason.validGoodsGroups.length > 0 && uniqueGoodsGroupCodes.every(code => validGoodsGroupsSet.has(code)));

                if (allValid) {
                    setDisposalReasonGismt(entity);
                    setData(prev => ({ ...prev, idDisposalReasonGismtGlobal: entity.idGlobal }));
                    setter?.(entity.idGlobal);
                } else {
                    renderGlobalAlert({
                        variant: "error",
                        statusCode: 0,
                        title: t("withdrawal.invalidGoodsGroups"),
                    });
                }

                setTimeout(() => {
                    isDisposalReasonGismtLoadingRef.current = false;
                }, 300);
            });
        } else {
            setDisposalReasonGismt(null);
        }
    };

    const onclearDisposalReasonGismt = () => {
        setDisposalReasonGismt(null);
    };

    return {
        disposalReasonGismt,
        disposalReasonGismtEnabled,
        disposalGismtForUnscannedLotsMode,
        disposalGismtForUnscannedLotsModeEnabled,
        updateDisposalReasonGismt,
        onclearDisposalReasonGismt,
        updateDisposalGismtForUnscannedLotsMode
    };
};